// Uniform-grid spatial hash for broad-phase collision (plan §4.5 / §9-C).
// A grid (not a quadtree) is the right structure here: many small, fast, uniformly
// distributed entities - the grid's best case. Rebuilt every tick (everything moves).
//
// Allocation discipline: cell arrays are recycled via a free list across frames, and
// query() writes ids into a caller-provided buffer instead of taking a closure, so the
// hot collision loop allocates nothing. Ship with Map; profile before going to typed
// arrays (plan §9-C).

export class SpatialHash {
  private readonly cells = new Map<number, number[]>();
  private readonly free: number[][] = [];
  private readonly inUse: number[][] = [];

  constructor(private readonly cellSize: number) {}

  // Cell coords fit in 16 bits for our arena/cell-size, so this key is collision-free.
  private hash(cx: number, cy: number): number {
    return (cx & 0xffff) * 65536 + (cy & 0xffff);
  }

  clear(): void {
    for (let i = 0; i < this.inUse.length; i++) {
      const a = this.inUse[i];
      a.length = 0;
      this.free.push(a);
    }
    this.inUse.length = 0;
    this.cells.clear();
  }

  insert(id: number, x: number, y: number): void {
    const k = this.hash(Math.floor(x / this.cellSize), Math.floor(y / this.cellSize));
    let arr = this.cells.get(k);
    if (arr === undefined) {
      arr = this.free.pop() ?? [];
      this.cells.set(k, arr);
      this.inUse.push(arr);
    }
    arr.push(id);
  }

  /**
   * Write the ids of all entities in cells overlapping the circle (x,y,r) into `out`
   * (reused buffer). Returns the count written. Broad-phase only - caller does the
   * narrow-phase distance test.
   */
  query(x: number, y: number, r: number, out: number[]): number {
    const cs = this.cellSize;
    const minCx = Math.floor((x - r) / cs);
    const maxCx = Math.floor((x + r) / cs);
    const minCy = Math.floor((y - r) / cs);
    const maxCy = Math.floor((y + r) / cs);
    let n = 0;
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        const arr = this.cells.get(this.hash(cx, cy));
        if (arr !== undefined) {
          for (let i = 0; i < arr.length; i++) out[n++] = arr[i];
        }
      }
    }
    return n;
  }
}
